import { useState, useEffect, useCallback } from 'react'
import { useToast } from '@/hooks/use-toast'
import {
  ClinicProgramOption,
  getClinicProgramOptions,
  deleteClinicProgramOption,
} from '@/services/clinicProgramOptionsService'

export interface UseGuidedProgramOptionsReturn {
  options: ClinicProgramOption[]
  loading: boolean
  isDeleting: boolean
  deleteTarget: ClinicProgramOption | null
  setDeleteTarget: (option: ClinicProgramOption | null) => void
  fetchOptions: () => Promise<void>
  confirmDelete: () => Promise<void>
}

export function useGuidedProgramOptions(
  clinicId: string,
  programId: string,
  enabled = true
): UseGuidedProgramOptionsReturn {
  const { toast } = useToast()
  const [options, setOptions] = useState<ClinicProgramOption[]>([])
  const [loading, setLoading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ClinicProgramOption | null>(null)

  const fetchOptions = useCallback(async () => {
    if (!enabled || !clinicId || !programId) return
    try {
      setLoading(true)
      const data = await getClinicProgramOptions(clinicId, programId)
      setOptions(data?.options ?? [])
    } catch {
      toast({ title: 'Error', description: 'Failed to load program options', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }, [clinicId, programId, enabled, toast])

  useEffect(() => {
    fetchOptions()
  }, [fetchOptions])

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return
    try {
      setIsDeleting(true)
      await deleteClinicProgramOption(clinicId, programId, deleteTarget.id)
      toast({ title: 'Removed', description: `"${deleteTarget.label}" has been removed` })
      setDeleteTarget(null)
      await fetchOptions()
    } catch {
      toast({ title: 'Error', description: 'Failed to remove option', variant: 'destructive' })
    } finally {
      setIsDeleting(false)
    }
  }, [clinicId, programId, deleteTarget, fetchOptions, toast])

  return { options, loading, isDeleting, deleteTarget, setDeleteTarget, fetchOptions, confirmDelete }
}
